import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/primitives/form";
import { Input } from "@/components/primitives/input";
import { FormStepProps } from "@/components/shared/Wizard/Wizard";
import {
  getChangePasswordSchema,
  ChangePasswordType,
} from "@/schemas/changePassword";
import { useForm } from "react-hook-form";
import { Button } from "@/components/primitives/button";
import { useTranslation } from "react-i18next";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import { useAuthState } from "@/zustand/AuthState";
import { cn } from "@/lib/utils";
import { MAX_PASSWORD_LENGTH } from "@/config/constants";

export type ChangePasswordStepData = {
  username: string;
  encryptedPassword: string;
  password: string;
};

export default function NewPasswordStep({
  onNext,
  onClose,
  updateData,
}: FormStepProps<Partial<ChangePasswordStepData>>) {
  const username = useAuthState((state) => state.username);
  const ePrK = useAuthState((state) => state.ePrK);
  const encryptedPassword = useAuthState((state) => state.encryptedPassword);
  const { t } = useTranslation();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const form = useForm<ChangePasswordType>({
    resolver: zodResolver(getChangePasswordSchema(username, ePrK, t)),
    defaultValues: {
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
  });

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };
  function handleForgotPassword() {
    if (!encryptedPassword) {
      return;
    }
    updateData({
      username: username,
      encryptedPassword: encryptedPassword,
    });
    onNext("RecoveryPhrase");
  }

  async function onSubmit(data: ChangePasswordType) {
    updateData({ ...data, username: username });
    onNext("Words");
  }
  return (
    <div className="flex size-full flex-col items-center justify-center gap-4">
      <h1 className="flex items-center self-start text-lg">
        {t("changePassword.title")}
      </h1>
      <p className="mb-6 w-full text-sm font-light opacity-50">
        {t("changePassword.description")}
      </p>
      <Form {...form}>
        <form
          className="flex w-full flex-col items-stretch gap-2"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="oldPassword"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel>{t("oldPassword")}</FormLabel>
                <FormControl>
                  <div className="relative w-full">
                    <Input
                      className="bg-card"
                      type={showOldPassword ? "text" : "password"}
                      maxLength={MAX_PASSWORD_LENGTH}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
                      onClick={toggleOldPasswordVisibility}
                    >
                      {showOldPassword ? (
                        <EyeIcon className="size-5 text-foreground/50" />
                      ) : (
                        <EyeOffIcon className="size-5 text-foreground/50" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("newPassword")}</FormLabel>
                <FormControl>
                  <div className="relative w-full">
                    <Input
                      className="bg-card"
                      type={showNewPassword ? "text" : "password"}
                      maxLength={MAX_PASSWORD_LENGTH}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
                      onClick={toggleNewPasswordVisibility}
                    >
                      {showNewPassword ? (
                        <EyeIcon className="size-5 text-foreground/50" />
                      ) : (
                        <EyeOffIcon className="size-5 text-foreground/50" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("confirmNewPassword")}</FormLabel>
                <FormControl>
                  <Input
                    className="bg-card"
                    type={showNewPassword ? "text" : "password"}
                    maxLength={MAX_PASSWORD_LENGTH}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex w-full items-center gap-4">
            <Button
              variant="link"
              type="button"
              className={cn(
                "text-foreground underline",
                encryptedPassword ? null : "hidden",
              )}
              onClick={handleForgotPassword}
            >
              {t("forgotPassword")}
            </Button>
            <span className="flex-1"></span>
            <div className="mt-4 flex items-center justify-end gap-4">
              {onClose && (
                <Button type="button" onClick={onClose} variant="outline">
                  {t("cancel")}
                </Button>
              )}
              <Button type="submit">{t("next")}</Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
